<template>
  <div class="w-full p-4 flex flex-col items-center">
    <div class="w-full flex items-center h-24px">
      <DiamondCount
        clickable
        class="absolute"
      />

      <span class="text-17px leading[1.4] mx-auto">
        {{ _t('package.unlock') }}
      </span>

      <Icon
        name="close-circle"
        class="cursor-pointer absolute right-4 z-1 w-25px"
        @click="emit('done')"
      ></Icon>
    </div>

    <div class="mx-auto px-4 flex flex-col items-center my-8 gap-10px">
      <PackageCover
        :package-style="props.package.style"
        :size="99"
        :border-radius="12"
      />

      <div
        class="text-19px leading-[1.2] font-semibold line-clamp-2"
        :style="{
          color: `var(--${props.package.style.themeColor}-800)`,
        }"
      >
        {{ props.package.name }}
      </div>
    </div>

    <template v-if="props.package.packageAccess === PackageAccess.Author">
      <div class="text-21px text-ld-brand-500 font-semibold mb-4">
        {{ _t('package.own_tip') }}
      </div>
    </template>

    <template v-if="props.package.packageAccess === PackageAccess.Unlock">
      <div class="text-21px text-ld-brand-500 font-semibold mb-4">
        {{ _t('package.unlocked') }}
      </div>
    </template>

    <template v-else>
      <div
        v-if="store.isVipValid"
        class="text-ld-premium-800 leading-[1.4] font-semibold text-center"
      >
        <div>{{ _t('vip.valid_tip2') }}</div>
        <div>{{ _t('vip.learn_tip2') }}</div>
      </div>

      <div
        v-else-if="store.vip != null"
        class="text-17px text-[var(--text-color-secondary)] leading-[1.4] font-semibold text-center mb-1"
      >
        {{ _t('vip.expired_tip') }}
      </div>

      <Button
        v-if="!store.isVipValid && defaultVipSku != null"
        class="w-full"
        scene="vip"
        twoLines
        @click="onVipBuy"
      >
        <div class="flex-1 flex flex-col items-center font-semibold">
          <div>{{ _t('vip.buy') }}</div>
          <div class="text-13px leading-14px">
            {{ _t('vip.buy_tip') }}
          </div>
        </div>
      </Button>

      <Button
        class="w-full mt-4"
        :loading="unlockLoading"
        twoLines
        @click="onUnlockByDiamonds"
      >
        <div class="flex-1 flex flex-col items-center font-semibold">
          <div class="">{{ _t('package.unlock_by_diamond') }}</div>

          <div class="flex items-center">
            <Icon
              name="diamond"
              class="w-14px mr-1"
            />
            <span class="text-13px leading-14px">
              {{ unlockDiamonds }}
            </span>
          </div>
        </div>
      </Button>
    </template>
  </div>
</template>
<script setup lang="ts">
import { fetchVipProducts, type VipSku } from '@/api/product'
import { computed, ref } from 'vue'
import {
  type PackageBasic,
  PackageAccess,
  unlockPackageByDiamonds,
} from '@/api/package-source'
import { useCommonStore } from '@/stores/common-store'
import { Code } from '@/api/code'
import DiamondCount from '@/components/DiamondCount.vue'
import { openStore } from '@/shared'
import PackageCover from '@/components/business/package/PackageCover.vue'

const store = useCommonStore()

const props = withDefaults(
  defineProps<{
    package: PackageBasic
    exitAfterUnlock?: boolean
  }>(),
  {
    exitAfterUnlock: false,
  }
)

const emit = defineEmits<{
  done: []
  unlock: [PackageAccess]
}>()

const vipSkuList = ref<VipSku[]>([])
const unlockDiamonds = ref(props.package.unlockDiamondRequirement)
const unlockLoading = ref(false)

const defaultVipSku = computed(() =>
  vipSkuList.value.find(item => item.defaultSelected)
)

store.fetchUserVip()
fetchVipProducts().then(res => {
  vipSkuList.value = res.productList
})

function onVipBuy() {
  openStore({
    exitAfterVipBought: true,
    onVipBought() {
      emit('unlock', PackageAccess.Vip)

      if (props.exitAfterUnlock) {
        emit('done')
      }
    },
  })
}

async function onUnlockByDiamonds() {
  unlockLoading.value = true
  unlockPackageByDiamonds(props.package.id)
    .then(res => {
      if (res.code !== Code.Ok) {
        _message.info(res.message)

        if (res.code === Code.NotEnoughDiamonds) {
          _openDiamondGetDialog()
        }
        return
      }

      _message.info(_t('package.unlock_success'))
      store.fetchUserAssets()
      emit('unlock', PackageAccess.Unlock)

      if (props.exitAfterUnlock) {
        emit('done')
      }
    })
    .finally(() => {
      unlockLoading.value = false
    })
}
</script>
<style scoped></style>
